import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAnimals } from "../../../../hooks/useAnimals";
import {
  getDisplayKupeNo,
  getDisplaySiparisNo,
} from "../../../../lib/animalLabels";
import {
  clearSessionUser,
  getDefaultPathForRole,
  readSessionUser,
  restoreSessionFromApi,
  type SessionUser,
} from "../../../../lib/authSession";
import {
  getSlaughterOps,
  rehydrateSlaughterOps,
  subscribeSlaughterOps,
  upsertSlaughterOp,
  type SlaughterOpStatus,
} from "../../../../lib/slaughterOpsStore";

type OperatorRole =
  | "Sıra Operatörü"
  | "Kesim Operatörü"
  | "Parçalama Operatörü"
  | "Tamamlama Operatörü";

type ColumnStatus = SlaughterOpStatus;
type ActiveStatus = Exclude<ColumnStatus, "done">;

interface RowItem {
  animalId: number;
  kupeNo: number;
  kupeNoLabel: string;
  siparisNo: string;
  tur: string;
  toplamHisse: number;
  satilanHisse: number;
  status: ColumnStatus;
  packingZone?: number | null;
  queuedAt?: string;
  startedAt?: string;
  cutAt?: string;
  packedAt?: string;
  doneAt?: string;
  previousStatus?: ActiveStatus;
}

interface StationConfig {
  role: OperatorRole;
  title: string;
  subtitle: string;
  status: ColumnStatus;
  actionLabel: string;
  actionVariant?: "primary" | "success" | "warning";
  nextStatus?: ColumnStatus;
  allowDirectDone?: boolean;
  allowPackingZone?: boolean;
}

function toNumber(value: string) {
  const parsed = Number(String(value ?? "").replace(/[^\d]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatTime(value?: string) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
}

function getTimeText(row: RowItem) {
  if (row.status === "queued") return formatTime(row.queuedAt);
  if (row.status === "entered") return formatTime(row.startedAt);
  if (row.status === "cut") return formatTime(row.cutAt);
  if (row.status === "packing") return formatTime(row.packedAt);
  return formatTime(row.doneAt);
}

function ActionButton({
  children,
  onClick,
  variant = "primary",
}: {
  children: React.ReactNode;
  onClick: () => void;
  variant?: "primary" | "success" | "warning";
}) {
  const className =
    variant === "success"
      ? "bg-farm-green text-white border-farm-green"
      : variant === "warning"
      ? "bg-farm-gold text-white border-farm-gold"
      : "bg-farm-earth text-white border-farm-earth";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-10 px-4 rounded-xl border text-sm font-semibold ${className}`}
    >
      {children}
    </button>
  );
}

export default function OperatorStationPage({ config }: { config: StationConfig }) {
  const navigate = useNavigate();
  const { animals, setAllAnimals } = useAnimals();
  const [version, setVersion] = useState(0);
  const [sessionUser, setSessionUser] = useState<SessionUser | null>(() => readSessionUser());
  const [packingTarget, setPackingTarget] = useState<RowItem | null>(null);

  useEffect(() => {
    let active = true;
    async function boot() {
      const local = readSessionUser();
      if (!local) {
        clearSessionUser();
        navigate("/giris", { replace: true });
        return;
      }
      if (active) setSessionUser(local);
      const restored = await restoreSessionFromApi();
      if (!active) return;
      if (!restored) {
        clearSessionUser();
        navigate("/giris", { replace: true });
        return;
      }
      if (
        restored.rol !== config.role &&
        restored.rol !== "Admin" &&
        restored.rol !== "Muhasebe"
      ) {
        navigate(getDefaultPathForRole(restored.rol), { replace: true });
        return;
      }
      setSessionUser(restored);
    }
    void boot();
    return () => {
      active = false;
    };
  }, [config.role, navigate]);

  useEffect(() => {
    void rehydrateSlaughterOps();
    return subscribeSlaughterOps(() => setVersion((value) => value + 1));
  }, []);

  const rows = useMemo<RowItem[]>(() => {
    const ops = getSlaughterOps();
    const opMap = new Map(ops.map((item) => [item.animalId, item]));

    return animals
      .filter((animal) => {
        const op = opMap.get(animal.id);
        if (!animal.kesimeUygun && !op) return false;
        if (!animal.kesimeUygun && op?.status === "queued" && op?.hidden) return false;
        return true;
      })
      .map((animal) => {
        const op = opMap.get(animal.id);
        const status = (op?.status ?? "queued") as ColumnStatus;
        return {
          animalId: animal.id,
          kupeNo: toNumber(getDisplayKupeNo(animal)),
          kupeNoLabel: getDisplayKupeNo(animal),
          siparisNo: getDisplaySiparisNo(animal),
          tur: animal.tur,
          toplamHisse: animal.toplamHisse,
          satilanHisse: animal.satirlanHisse,
          status,
          packingZone: status === "packing" ? Number(op?.packingZone ?? 0) || null : null,
          queuedAt: op?.queuedAt,
          startedAt: op?.startedAt,
          cutAt: op?.cutAt,
          packedAt: op?.packedAt,
          doneAt: op?.doneAt,
          previousStatus: op?.previousStatus as ActiveStatus | undefined,
        };
      })
      .filter((row) => Number.isFinite(row.kupeNo) && row.kupeNo > 0)
      .filter((row) => row.status === config.status)
      .sort((a, b) => a.kupeNo - b.kupeNo);
  }, [animals, version, config.status]);

  async function setStatus(row: RowItem, status: ColumnStatus, packingZone?: number | null) {
    const now = new Date().toISOString();
    const nextRecord =
      status === "queued"
        ? {
            animalId: row.animalId,
            status,
            hidden: false,
            previousStatus: undefined,
            queuedAt: row.queuedAt ?? now,
            startedAt: undefined,
            cutAt: undefined,
            packedAt: undefined,
            doneAt: undefined,
            packingZone: null,
          }
        : status === "entered"
        ? {
            animalId: row.animalId,
            status,
            hidden: false,
            previousStatus: undefined,
            queuedAt: row.queuedAt ?? now,
            startedAt: row.startedAt ?? now,
            cutAt: undefined,
            packedAt: undefined,
            doneAt: undefined,
            packingZone: null,
          }
        : status === "cut"
        ? {
            animalId: row.animalId,
            status,
            hidden: false,
            previousStatus: undefined,
            queuedAt: row.queuedAt ?? now,
            startedAt: row.startedAt ?? now,
            cutAt: row.cutAt ?? now,
            packedAt: undefined,
            doneAt: undefined,
            packingZone: null,
          }
        : status === "packing"
        ? {
            animalId: row.animalId,
            status,
            hidden: false,
            previousStatus: undefined,
            queuedAt: row.queuedAt ?? now,
            startedAt: row.startedAt ?? now,
            cutAt: row.cutAt ?? now,
            packedAt: row.packedAt ?? now,
            doneAt: undefined,
            packingZone: packingZone ?? row.packingZone ?? 1,
          }
        : {
            animalId: row.animalId,
            status,
            hidden: false,
            previousStatus: row.status === "done" ? row.previousStatus ?? "cut" : (row.status as ActiveStatus),
            queuedAt: row.queuedAt ?? now,
            startedAt: row.startedAt ?? now,
            cutAt:
              row.cutAt ??
              (row.status === "cut" || row.status === "packing" || row.status === "done"
                ? now
                : undefined),
            packedAt:
              row.status === "packing" || row.previousStatus === "packing"
                ? row.packedAt ?? now
                : undefined,
            doneAt: now,
            packingZone: row.status === "packing" ? row.packingZone ?? null : null,
          };

    await upsertSlaughterOp(nextRecord);

    await setAllAnimals((prev) =>
      prev.map((animal) => {
        if (animal.id !== row.animalId) return animal;

        if (status === "queued" || status === "entered") {
          return {
            ...animal,
            kesimeUygun: true,
            tamamlandi: false,
            durum: animal.durum === "Teslim Edildi" ? animal.durum : "Kesim Bekliyor",
          };
        }

        if (status === "cut" || status === "packing") {
          return {
            ...animal,
            kesimeUygun: true,
            tamamlandi: false,
            durum: animal.durum === "Teslim Edildi" ? animal.durum : "Kesildi",
          };
        }

        return {
          ...animal,
          kesimeUygun: true,
          tamamlandi: true,
          durum: animal.durum === "Teslim Edildi" ? animal.durum : "Kesildi",
        };
      })
    );
  }

  function logout() {
    clearSessionUser();
    navigate("/giris", { replace: true });
  }

  if (!sessionUser) return null;

  return (
    <div className="min-h-screen bg-farm-cream font-body">
      <div className="max-w-6xl mx-auto px-4 py-5 space-y-4">
        <div className="bg-white border border-farm-border rounded-farm px-4 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <h1 className="font-heading font-bold text-lg text-farm-brown">{config.title}</h1>
              <p className="text-sm text-farm-muted mt-1">{config.subtitle}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <div className="inline-flex items-center gap-2 rounded-xl border border-farm-border bg-farm-cream px-3 py-2 text-xs text-farm-brown">
                <i className="ri-user-line" />
                {sessionUser.adSoyad}
              </div>
              <button type="button" onClick={() => navigate('/sifre-degistir')} className="h-10 px-4 rounded-xl border border-farm-border bg-white text-farm-brown text-sm font-semibold">
                Şifre Değiştir
              </button>
              <button type="button" onClick={logout} className="h-10 px-4 rounded-xl border border-red-200 bg-red-50 text-red-600 text-sm font-semibold">
                Çıkış Yap
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white border border-farm-border rounded-farm px-4 py-3 flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-farm-muted">Bekleyen Kayıt</p>
            <p className="text-2xl font-bold text-farm-brown mt-1">{rows.length}</p>
          </div>
          <div className="text-right text-xs text-farm-muted">
            Sadece kendi istasyonundaki hayvanları görürsün.<br />
            Ana kesim alanı ve TV akışı değişmez.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {rows.length === 0 ? (
            <div className="col-span-full rounded-farm border border-dashed border-farm-border bg-white px-4 py-12 text-center text-sm text-farm-muted">
              Bu istasyonda bekleyen hayvan yok.
            </div>
          ) : (
            rows.map((row) => (
              <div key={row.animalId} className="border border-farm-border rounded-xl bg-white px-3 py-3 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-lg font-bold text-farm-brown leading-none">{row.kupeNoLabel}</span>
                      <span className="inline-flex items-center rounded-full border border-farm-border bg-farm-cream px-2 py-0.5 text-[10px] text-farm-muted">{row.tur}</span>
                      {row.status === "packing" && row.packingZone && (
                        <span className="inline-flex items-center rounded-full border border-farm-gold/20 bg-farm-gold-pale px-2 py-0.5 text-[10px] font-semibold text-farm-gold">
                          Parçalama {row.packingZone}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 text-sm text-farm-muted">Sipariş: {row.siparisNo}</div>
                    <div className="mt-1 text-sm text-farm-muted">{row.satilanHisse}/{row.toplamHisse} hisse</div>
                  </div>
                  <div className="text-right text-xs text-farm-muted shrink-0">
                    <div>Zaman</div>
                    <div className="text-farm-brown font-semibold mt-1">{getTimeText(row)}</div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-farm-border/70 flex items-center gap-2 flex-wrap justify-end">
                  {config.allowPackingZone ? (
                    <>
                      <ActionButton onClick={() => setPackingTarget(row)} variant="primary">Parçalamaya Al</ActionButton>
                      <ActionButton onClick={() => void setStatus(row, "done")} variant="success">Direkt Tamamla</ActionButton>
                    </>
                  ) : (
                    <ActionButton onClick={() => void setStatus(row, config.nextStatus ?? config.status)} variant={config.actionVariant}>{config.actionLabel}</ActionButton>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {packingTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white border border-farm-border shadow-xl">
            <div className="px-4 py-3 border-b border-farm-border">
              <h2 className="font-heading text-sm font-bold text-farm-brown">Parçalama Bölümü Seç</h2>
              <p className="text-[11px] text-farm-muted mt-1">{packingTarget.kupeNoLabel} için parçalama alanını seç.</p>
            </div>
            <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {[1,2,3,4,5,6].map((zone) => (
                <button key={zone} type="button" onClick={() => { void setStatus(packingTarget, "packing", zone); setPackingTarget(null); }} className="h-11 rounded-xl border border-farm-border bg-farm-cream text-farm-brown text-sm font-semibold hover:border-farm-earth hover:bg-farm-gold-pale transition-colors">
                  Parçalama {zone}
                </button>
              ))}
            </div>
            <div className="px-4 pb-4 flex justify-end">
              <button type="button" onClick={() => setPackingTarget(null)} className="h-10 px-4 rounded-xl border border-farm-border bg-white text-farm-muted text-sm font-semibold hover:bg-farm-cream">İptal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
